import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import { saveDraft } from "../../components/flight-booking/BookingShared";
import FlightSearchForm from "../../components/flight-search/FlightSearchForm";
import FlightSearchResults from "../../components/flight-search/FlightSearchResults";
import { fareSearchService } from "../../services/fare-search.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";
import DContext from "../../context/DContext";
import { checkIsAdmin } from "../../config/siteConfig";

const ACCENT = "var(--btn-bg)";

// The last search (and its results) is kept only so the page can be restored when the user comes back from the booking page
export const SEARCH_STATE_KEY = "flight-search-state";
export const SEARCH_FORM_KEY = "flight-search-form";
const readSaved = (key) => {
  try {
    return JSON.parse(sessionStorage.getItem(key));
  } catch (err) {
    return null;
  }
};

const FlightSearch = () => {
  const { authState } = useContext(DContext);
  const navigate = useNavigate();
  const location = useLocation();
  // Only arriving back from the booking page restores the previous search; any other visit starts fresh
  const [restore] = useState(() => !!location.state?.restore);
  const [saved] = useState(() => (restore ? readSaved(SEARCH_STATE_KEY) : null));
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(!!saved);
  const [results, setResults] = useState(saved?.results || []);
  const [lastSearch, setLastSearch] = useState(saved?.lastSearch || null);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (restore) {
      // Restored once: drop the marker so a page refresh starts fresh
      navigate("/flight-search", { replace: true, state: null });
      return;
    }
    sessionStorage.removeItem(SEARCH_STATE_KEY);
    sessionStorage.removeItem(SEARCH_FORM_KEY);
  }, [restore, navigate]);
  const isAdmin = checkIsAdmin(authState.user_role);

  const showSwal = (title, text, icon) => withReactContent(Swal).fire({ title, text, icon });

  const handleSearch = async (payload) => {
    try {
      setLoading(true);
      setSearched(true);
      setLastSearch(payload);
      // only the fields in FareSearchPayload go to the API; the rest of the search is kept for display/booking
      const { trip_type, airlines, sector, pax_detail } = payload;
      const res = await fareSearchService.search({
        trip_type,
        airlines: airlines || [],
        sector: {
          origin: sector.origin,
          destination: sector.destination,
          departure_date: sector.departure_date,
          cabin_class: sector.cabin_class,
        },
        pax_detail,
      });
      if (res?.success) {
        const list = Array.isArray(res.resData) ? res.resData : [];
        setResults(list);
      } else {
        setResults([]);
        toast.error(res?.message);
      }
    } catch (err) {
      setResults([]);
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
    }
  };

  // Book Now: open the traveller details page for the chosen fare
  const handleBook = (fare) => {
    const draft = { fare, search: lastSearch, pax: lastSearch?.pax_detail };
    saveDraft(draft);
    try {
      sessionStorage.setItem(SEARCH_STATE_KEY, JSON.stringify({ results, lastSearch }));
    } catch (err) {
      // storage full/unavailable: the search simply will not be restored
    }
    // Mark this history entry so the browser Back button also restores the search
    navigate("/flight-search", { replace: true, state: { restore: true } });
    navigate("/flight-booking", { state: draft });
  };

  // Clear the results and put the search form back to its defaults
  const handleReset = () => {
    setResults([]);
    setSearched(false);
    setLastSearch(null);
    sessionStorage.removeItem(SEARCH_FORM_KEY);
    sessionStorage.removeItem(SEARCH_STATE_KEY);
    navigate("/flight-search", { replace: true, state: null });
    setResetKey((k) => k + 1);
  };

  return (
    <div className="flight-search-page">
      <style>{pageCss(isAdmin)}</style>

      <section className="fs-hero">
        <FlightSearchForm onSearch={handleSearch} loading={loading} onReset={handleReset} resetKey={resetKey} restore={restore} />
      </section>

      <section className="mt-2">
        <FlightSearchResults results={results} loading={loading} searched={searched} search={lastSearch} onBook={handleBook} />
      </section>
    </div>
  );
};

const pageCss=(isAdmin) => `
.flight-search-page { background:#eef3f8; }
.main { background:#eef3f8; }
.fs-hero { background:#fff; border:1px solid #d5dbe8; border-radius:12px; padding:12px 16px 16px; position:relative; box-shadow:0 2px 8px rgba(16,24,40,.06); }

.fr-summary { display:flex; flex-wrap:wrap; justify-content:space-between; align-items:baseline; gap:4px 12px; margin-bottom:6px; }
.fr-heading { font-size:15px; font-weight:700; color:#0f172a; }
.fr-muted { font-size:12px; color:#64748b; }
.fr-muted-city { font-size:11px; color:#0f172a;margin-left: 1px; }
.fr-green-city { font-size:12px; color: var(--btn-bg);font-weight:700; }
.fr-muted-odshow { font-size:10px; color:#64748b;overflow: hidden;white-space: nowrap;text-overflow: ellipsis; }
.fr-card { position:relative; margin-top:14px; background:#fff; border-radius:10px; box-shadow:0 1px 2px rgba(16,24,40,.05); margin-bottom:0; border:1px solid #d5dbe8; transition:box-shadow .15s, border-color .15s; font-family:Inter, system-ui, sans-serif; }
.fr-card:hover { border-color:#a7d8d0; box-shadow:0 2px 8px rgba(16,24,40,.07); }
// .fr-row { display:grid; grid-template-columns:20fr 33fr 20fr 14fr 13fr; align-items:center; padding:10px 4px; }
.fr-row { display:grid; grid-template-columns:${isAdmin ? "20fr 60fr 20fr" : "20fr 50fr 15fr 15fr"}; align-items:center; padding:10px 4px; }
.fr-col { padding:0 14px; border-left:1px solid #eef0f5; min-width:0; }
.fr-col:first-child { border-left:none; }
.fr-col-actions { border-left:none; }
.fr-badge { display:inline-block; font-size:10px; font-weight:700; letter-spacing:.5px; padding:1px 7px; border-radius:5px; background:#e6f1f8; color:${ACCENT}; margin-bottom:4px; }
.fr-badge-outline { background:#fff; border:1px solid #d7dbe6; color:#64748b; letter-spacing:0; font-weight:600; margin:2px 0; }
.fr-route-codes { font-size:19px; font-weight:700; color:#0f172a; line-height:1.2; white-space:nowrap; }
.fr-route-codes i { font-size:12px; color:#64748b; margin:0 3px; }
.fr-airline { display:flex; align-items:center; gap:8px; min-width:0; margin-bottom:2px; }
.fr-logo { width:28px; height:28px; border-radius:6px; object-fit:contain; background:#f8f9fb; flex-shrink:0; }
.fr-logo-fallback { display:flex; align-items:center; justify-content:center; font-weight:700; color:var(--btn-hover-bg); font-size:11px; }
.fr-airline-name { font-size:13px; font-weight:600; color:#0f172a; line-height:1.25; }
.fr-date { font-size:13px; color:#0f172a; line-height:1.2;font-weight:700;margin-bottom: 5px; }
.fr-timeline { display:grid; grid-template-columns:auto 1fr auto; gap:10px; align-items:center; }
.fr-time { font-size:17px; font-weight:700; color:#0f172a; line-height:1.15; }
.fr-timeline .fr-muted { font-size:11px; line-height:1.2; }
.fr-dayshift { color:${ACCENT}; font-size:10px; margin-left:2px; }
.fr-path { text-align:center; min-width:64px; }
.fr-path .fr-muted { line-height:1.1; }
.fr-line { position:relative; height:0; border-top:1px solid #cbd5e1; margin:4px 0; }
.fr-line-end { position:absolute; top:-4px; width:7px; height:7px; border-radius:50%; border:1.5px solid ${ACCENT}; background:#fff; }
.fr-line-end.start { left:0; }
.fr-line-end.end { right:0; background:${ACCENT}; }
.fr-line-stop { position:absolute; left:50%; top:-5px; width:8px; height:8px; border-radius:50%; background:#fff; border:2px solid ${ACCENT}; transform:translateX(-50%); }
.fr-stops { font-size:11px; font-weight:600; line-height:1.1; }
.fr-avail-title { font-size:14px; font-weight:600; color:#0f172a; margin-bottom:4px; }
.fr-avail-badge { display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:600; padding:1px 9px; border-radius:999px; background:#ecfdf3; color:#15803d; margin-bottom:5px; }
.fr-avail-badge.low { background:#fff4e5; color:#c2410c; }
.fr-progress { height:5px; border-radius:999px; background:#e5e7eb; overflow:hidden; margin:2px 0 6px; }
.fr-progress span { display:block; height:100%; background:var(--btn-bg); border-radius:999px; }
.fr-progress.low span { background:#d97706; }
.fr-seat-stats { display:flex; gap:12px; }
.fr-seat-stats b { color:#0f172a; font-weight:600; }
.fr-series-tag { margin-left:6px; font-size:9px; padding:1px 6px; border:1px solid #d7dbe6; border-radius:4px; color:#475569; letter-spacing:.6px; }
.fr-avail-dot { width:6px; height:6px; border-radius:50%; background:currentColor; }
.fr-corner-tag { position:absolute; top:-10px; left:14px; z-index:1; display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:600; line-height:1; padding:4px 10px; border-radius:999px; background:#fff; border:1px solid currentColor; }
.fr-corner-tag.yes { color:#15803d; background:#f3fbf6; }
.fr-corner-tag.no { color:#b91c1c; background:#fef6f6; }
.fr-fare-label { font-size:10px; font-weight:700; letter-spacing:.7px; color:#64748b; }
.fr-price { font-size:18px; font-weight:700; color:#0f172a; line-height:1.2; }
.fr-book-btn { width:100%; border:none; background:var(--btn-bg); color:#fff; border-radius:8px; font-size:13px; font-weight:600; height:34px; transition:background .15s; }
.fr-book-btn:hover { background:var(--btn-hover-bg); }
.fr-book-btn:focus-visible, .fr-details-btn:focus-visible { outline:2px solid ${ACCENT}; outline-offset:2px; }
.fr-details-btn { width:100%; border:1px solid #d7dbe6; background:#fff; color:#0f172a; border-radius:8px; font-size:13px; font-weight:500; height:34px; margin-top:6px; }
.fr-details-btn:hover { background:#f4fbf9; }
.fr-details { border-top:1px solid #d3e9e5; padding:14px 18px 16px; background:linear-gradient(180deg,#f4fbf9 0%,#fff 100%); border-radius:0 0 10px 10px; }
.fr-tabs { display:inline-flex; gap:4px; padding:3px; background:#f3f4f6; border-radius:9px; margin-bottom:14px; max-width:100%; overflow-x:auto; }
.fr-tab { border:none; background:none; padding:6px 14px; font-size:13px; font-weight:600; color:#64748b; border-radius:7px; white-space:nowrap; display:flex; align-items:center; gap:6px; }
.fr-tab:hover { color:#0f172a; }
.fr-tab.active { background:#fff; color:${ACCENT}; box-shadow:0 1px 3px rgba(16,24,40,.12); }
.fr-chips { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
.fr-chip { display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:500; color:#334155; background:#fff; border:1px solid #d5dbe8; border-radius:999px; padding:4px 12px; }
.fr-chip i { color:${ACCENT}; }
.fr-banner { display:flex; align-items:center; gap:12px; border-radius:10px; padding:12px 14px; border:1px solid; }
.fr-banner > i { font-size:22px; }
.fr-banner.yes { background:#f3fbf6; border-color:#bbf7d0; color:#15803d; }
.fr-banner.no { background:#fef6f6; border-color:#fecaca; color:#b91c1c; }
.fr-banner.info { background:#e6f1f8; border-color:#a7d8d0; color:var(--btn-bg); }
.fr-banner.info .fr-banner-title { color:#0f172a; }
.fr-banner-title { font-size:14px; font-weight:700; }
.fr-legs { display:flex; flex-direction:column; gap:8px; margin-bottom:12px; }
.fr-leg { display:flex; align-items:center; gap:12px; flex-wrap:wrap; background:#fff; border:1px solid #d5dbe8; border-left:3px solid ${ACCENT}; border-radius:8px; padding:8px 14px; font-size:14px; color:#0f172a; }
.fr-leg > i { color:#64748b; }
.fr-meta { display:flex; flex-wrap:wrap; gap:6px 20px; font-size:12px; color:#64748b; }
.fr-meta b { color:#334155; }
.fr-detail-title { font-size:11px; font-weight:700; letter-spacing:.6px; text-transform:uppercase; color:#6b7280; margin-bottom:6px; }
.fr-detail-text { font-size:13px; color:#374151; }
.fr-table { border:1px solid #d5dbe8; border-radius:8px; overflow:hidden; background:#fff; }
.fr-table th { font-size:11px; color:#64748b; font-weight:700; text-transform:uppercase; letter-spacing:.4px; background:#e6f1f8; border-bottom:1px solid #a7d8d0; }
.fr-table td { font-size:13px; }

.fr-empty { background:#fff; border-radius:10px; padding:40px 20px; text-align:center; border:1px solid #d5dbe8; }
.fr-empty-title { font-size:16px; font-weight:700; color:#111827; margin:8px 0 4px; }
.fr-skeleton { height:110px; background:linear-gradient(90deg,#fff 25%,#f3f4f6 50%,#fff 75%); background-size:200% 100%; animation:fr-shimmer 1.2s infinite; }
@keyframes fr-shimmer { 0% { background-position:200% 0; } 100% { background-position:-200% 0; } }
.min-w-0 { min-width:0; }

@media (max-width: 1199px) {
  .fr-col { padding:0 10px; }
  .fr-row { grid-template-columns:19fr 32fr 21fr 15fr 13fr; }
}
@media (max-width: 991px) {
  .fr-row { grid-template-columns:1fr 1.6fr; row-gap:10px; padding:10px 0; }
  .fr-col:nth-child(3) { border-left:none; }
  .fr-col-actions { grid-column:1 / -1; display:flex; gap:8px; border-top:1px solid #eef0f5; padding-top:10px; }
  .fr-col-actions button { margin-top:0; flex:1; }
}
@media (max-width: 767px) {
  .fs-hero { padding:10px 12px 12px; }
  .fr-row { grid-template-columns:1fr; padding:10px 0; row-gap:0; }
  .fr-col { border-left:none; border-top:1px solid #eef0f5; padding:10px 14px; }
  .fr-col:first-child { border-top:none; }
  .fr-col-fare { display:flex; flex-wrap:wrap; align-items:baseline; gap:2px 10px; }
  .fr-col-actions { padding-top:10px; }
  .fr-sort { padding:6px 8px; flex-direction:column; align-items:flex-start; gap:0; }
}
`;

export default FlightSearch;
